import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Save, RefreshCw, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SmsPricingSectionProps {
  organisationId: string;
  restaurants: any[];
}

export const SmsPricingSection: React.FC<SmsPricingSectionProps> = ({ 
  organisationId,
  restaurants
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pricingData, setPricingData] = useState<any[]>([]);
  const [newPrice, setNewPrice] = useState('0.15');
  const [currency, setCurrency] = useState('NZD');
  
  // Load pricing data when component mounts or organisationId changes
  useEffect(() => {
    if (organisationId) {
      loadPricingData();
    }
  }, [organisationId]);

  // Load pricing data from the database
  const loadPricingData = async () => {
    setLoading(true);
    try {
      // Fetch org-level pricing
      const { data: orgPricing, error: orgError } = await supabase
        .from('sms_price_configuration')
        .select('*')
        .eq('organisation_id', organisationId)
        .maybeSingle();
        
      if (orgError) throw orgError;
      
      // Fetch restaurant-level pricing
      const restaurantIds = restaurants.map(r => r.restaurant_id);
      
      const { data: restPricing, error: restError } = await supabase
        .from('sms_price_configuration')
        .select('*')
        .in('restaurant_id', restaurantIds);
        
      if (restError) throw restError;
      
      // Combine pricing data
      const pricingArray = [];
      
      if (orgPricing) {
        pricingArray.push({
          ...orgPricing,
          type: 'organisation',
          name: 'Organization Default'
        });
        
        // Set form defaults from org pricing
        setNewPrice(orgPricing.price_per_segment.toString());
        setCurrency(orgPricing.currency);
      }
      
      if (restPricing && restPricing.length > 0) {
        // Add restaurant info to pricing data
        const enhancedRestPricing = restPricing.map(pricing => {
          const restaurant = restaurants.find(r => r.restaurant_id === pricing.restaurant_id);
          return {
            ...pricing,
            type: 'restaurant',
            name: restaurant ? restaurant.restaurant_name : 'Unknown Restaurant'
          };
        });
        
        pricingArray.push(...enhancedRestPricing);
      }
      
      setPricingData(pricingArray);
    } catch (error) {
      console.error('Error loading pricing data:', error);
      toast({
        title: "Failed to load pricing data",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Save pricing configuration
  const savePricing = async () => {
    setSubmitting(true);
    try {
      // Validate input
      const priceNumeric = parseFloat(newPrice);
      if (isNaN(priceNumeric) || priceNumeric <= 0) {
        throw new Error('Please enter a valid price greater than 0');
      }
      
      // Save to database
      const { data, error } = await supabase
        .from('sms_price_configuration')
        .upsert({
          organisation_id: organisationId,
          restaurant_id: null, // Null for org-level pricing
          price_per_segment: priceNumeric,
          currency,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'organisation_id',
          ignoreDuplicates: false
        })
        .select();
        
      if (error) throw error;
      
      toast({
        title: "Pricing updated",
        description: "SMS pricing has been updated successfully",
      });
      
      // Reload pricing data
      loadPricingData();
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast({
        title: "Failed to update pricing",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>SMS Pricing Configuration</CardTitle>
          <CardDescription>
            Configure SMS pricing for this organisation and its restaurants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Organisation default pricing form */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Organisation Default Pricing</h3>
              <p className="text-sm text-muted-foreground">
                Set the default SMS price per message segment for the entire organisation.
                Individual restaurant pricing can override this default.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price per SMS segment</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="0.15"
                  />
                  <p className="text-xs text-muted-foreground">
                    The standard SMS segment price is $0.15 NZD
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NZD">NZD - New Zealand Dollar</SelectItem>
                      <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={savePricing} 
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Pricing
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Pricing list */}
            <div className="mt-8">
              <h3 className="text-lg font-medium mb-4">Current Pricing Configuration</h3>
              
              {loading ? (
                <div className="py-4 text-center">Loading pricing data...</div>
              ) : pricingData.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Price per Segment</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingData.map((pricing) => (
                      <TableRow key={pricing.id}>
                        <TableCell>{pricing.name}</TableCell>
                        <TableCell className="capitalize">{pricing.type}</TableCell>
                        <TableCell>${pricing.price_per_segment.toFixed(2)}</TableCell>
                        <TableCell>{pricing.currency}</TableCell>
                        <TableCell>
                          {new Date(pricing.updated_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setNewPrice(pricing.price_per_segment.toString());
                              setCurrency(pricing.currency);
                            }}
                          >
                            Use
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center border rounded-lg">
                  <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-medium">No Pricing Configuration</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    There's no SMS pricing configured for this organisation yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};